"use client";

import React, { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import Link from "next/link";
// import { useRouter } from "next/navigation"; // Ask Orane commented
import "./enterprise-data.css";

/* Images — public/assets/images/ se serve hoti hain */
const IMG = "/assets/images";
const img = (name: string) => encodeURI(`${IMG}/${name}`);
const imgHero = img("enterprise-data-hero.jpg");
const imgTurn = img("data-intelligence.jpg");
const imgCta = img("lets-banner.jpg");

/* All styling lives in enterprise-data.css (no styling in this file). */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400) =>
  ({ "--ed-delay": `${Math.min(i * step, max)}ms` } as React.CSSProperties);

/* ---------- Icons ---------- */

type IconKey =
  | "compass" | "nodes" | "stack" | "shield" | "cube" | "chart" | "search" | "arrow"
  | "bulb" | "briefcase" | "db" | "gear" | "eye" | "bolt" | "clock" | "check"
  | "trend" | "people" | "link" | "doc" | "target";

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    compass: (<><circle cx="12" cy="12" r="9" /><path d="m15.5 8.5-2 5-5 2 2-5 5-2Z" /></>),
    nodes: (<><circle cx="6" cy="6" r="2.2" /><circle cx="18" cy="6" r="2.2" /><circle cx="6" cy="18" r="2.2" /><circle cx="18" cy="18" r="2.2" /><circle cx="12" cy="12" r="2.4" /><path d="m7.6 7.6 2.7 2.7M16.4 7.6l-2.7 2.7M7.6 16.4l2.7-2.7M16.4 16.4l-2.7-2.7" /></>),
    stack: (<><path d="m12 3 9 5-9 5-9-5 9-5Z" /><path d="m3 12.5 9 5 9-5M3 16.5l9 5 9-5" /></>),
    shield: (<><path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" /><path d="m9 12 2.2 2.2L15.5 10" /></>),
    cube: (<><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9" /></>),
    chart: <path d="M5 20V12M11 20V4M17 20V9M3 20h18" />,
    search: (<><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4.5 4.5" /></>),
    arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
    bulb: (<><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3Z" /></>),
    briefcase: (<><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M3 13h18" /></>),
    db: (<><ellipse cx="12" cy="5.5" rx="7" ry="2.8" /><path d="M5 5.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6M5 11.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6" /></>),
    gear: (<><circle cx="12" cy="12" r="3" /><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" /></>),
    eye: (<><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.8" /></>),
    bolt: <path d="M13 3 5 13.5h6L10 21l8-10.5h-6L13 3Z" />,
    clock: (<><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>),
    check: (<><circle cx="12" cy="12" r="8.5" /><path d="m8.5 12.2 2.4 2.4 4.6-5" /></>),
    trend: (<><path d="M3 17l5-5 4 4 8-9" /><path d="M15 7h5v5" /></>),
    people: (<><circle cx="9" cy="8" r="3" /><path d="M3 20c.4-3.6 2.6-5.4 6-5.4s5.6 1.8 6 5.4M16 5.5a3 3 0 0 1 0 5.8M18 14.8c1.8.6 2.8 2.3 3 5.2" /></>),
    link: (<><path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" /><path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" /></>),
    doc: (<><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 9h6M9 13h6M9 17h3" /></>),
    target: (<><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1" /></>),
  };

  return (
    <svg
      className="ed-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

/* Ask Orane section commented out
const questions = [
  "Why did our margin fall?",
  "Where is our cash stuck?",
  "How accurate is our demand forecast?",
  "Can we trust our data?",
  "What is stopping month-end close?",
];

const askPoints: { icon: IconKey; text: string }[] = [
  { icon: "briefcase", text: "Business context, not just data" },
  { icon: "check", text: "Real enterprise experience" },
  { icon: "target", text: "Practical, measurable outcomes" },
  { icon: "people", text: "People + AI, working together" },
];
*/

const capabilities: { icon: IconKey; title: string; text: string; href: string }[] = [
  { icon: "compass", title: "Data Strategy & Advisory", text: "Define a clear data strategy aligned to business goals, decision needs and technology roadmap.", href: "/solutions/data-strategy" },
  { icon: "nodes", title: "Data Engineering & Integration", text: "Connect and prepare data from SAP, CRM, SFA and other enterprise sources for trusted analysis.", href: "/solutions/data-engineering" },
  { icon: "stack", title: "Data Platforms & Warehousing", text: "Design scalable data warehouses and lakehouses for analytics, historical analysis and real-time insights.", href: "/solutions/data-platforms" },
  { icon: "shield", title: "Data Governance & Quality", text: "Establish data quality rules, stewardship, lineage and governance for trusted and consistent data.", href: "/solutions/data-governance" },
  { icon: "cube", title: "Master & Reference Data", text: "Create a single, accurate view of customers, suppliers, products, materials and organisational data.", href: "/solutions/master-data" },
  { icon: "chart", title: "Analytics & AI Enablement", text: "Prepare data for business intelligence, predictive analytics and AI/ML use cases.", href: "/solutions/analytics-ai" },
];

const solutions: { icon: IconKey; title: string; text: string }[] = [
  { icon: "search", title: "Enterprise Data Assessment", text: "Identify data sources, quality gaps and business information requirements." },
  { icon: "link", title: "Data Integration & Pipelines", text: "Consolidate data from SAP and non-SAP systems with reliable, scalable pipelines." },
  { icon: "chart", title: "Analytics & Reporting", text: "Create dashboards, reports and self-service analytics for better decision-making." },
  { icon: "shield", title: "Data Governance & MDM", text: "Improve data quality, consistency and ownership." },
  { icon: "bolt", title: "AI-Ready Data Foundations", text: "Prepare governed, contextualised data for AI and advanced analytics." },
];

const steps = [
  { title: "Understand", text: "Identify business questions, data sources and current challenges." },
  { title: "Assess", text: "Evaluate data quality, gaps, integration needs and priorities." },
  { title: "Design", text: "Define data architecture, models, governance and use cases." },
  { title: "Build", text: "Implement data pipelines, platforms and analytics solutions." },
  { title: "Enable", text: "Deliver insights, support adoption and continuously improve." },
];

const benefits: { icon: IconKey; text: string }[] = [
  { icon: "eye", text: "One view of the business" },
  { icon: "bolt", text: "Faster decision-making" },
  { icon: "nodes", text: "Improved operational visibility" },
  { icon: "clock", text: "Reduced manual effort" },
  { icon: "shield", text: "Greater data confidence" },
  { icon: "trend", text: "Strong foundation for AI" },
];

const useCases = [
  { title: "Inventory & Working Capital", text: "Avoid unnecessary purchases and optimise stock levels.", image: img("margin-working-capital.jpg"), href: "/use-cases/inventory-working-capital" },
  { title: "Revenue & Margin Analysis", text: "Understand what's driving margin changes.", image: img("Higher-Margins.jpg"), href: "/use-cases/revenue-margin-analysis" },
  { title: "Cash & Receivables", text: "Identify where cash is stuck and improve collections.", image: img("COLLECT-&-CASH.jpg"), href: "/use-cases/cash-receivables" },
  { title: "Supply Chain Planning", text: "Improve demand forecasting and inventory decisions.", image: img("Demand.jpg"), href: "/use-cases/supply-chain-planning" },
  { title: "Customer Intelligence", text: "Identify at-risk customers and growth opportunities.", image: img("Customer-Feedback.jpg"), href: "/use-cases/customer-intelligence" },
  { title: "Enterprise Reporting", text: "Simplify and accelerate month-end close.", image: img("From-Reporting-to-Intelligence.jpg"), href: "/use-cases/enterprise-reporting" },
];

const why: { icon: IconKey; title: string; text: string }[] = [
  { icon: "bulb", title: "Business-first approach", text: "We start with the decisions you need to make." },
  { icon: "db", title: "Enterprise systems expertise", text: "Deep understanding of SAP, CRM and other business systems." },
  { icon: "stack", title: "End-to-end capabilities", text: "Strategy, integration, governance, analytics and AI enablement." },
  { icon: "gear", title: "Practical and scalable delivery", text: "Solutions designed for real business environments." },
  { icon: "trend", title: "Continuous value orientation", text: "We help you keep improving after go-live." },
];

/* ============================== PAGE ============================== */

export default function EnterpriseDataPage() {
  const ref = useScrollReveal();
  /* Ask Orane section commented out
  const router = useRouter();
  const [query, setQuery] = useState("");

  const onAsk = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    router.push(`/tellusyourproblem${q ? `?q=${encodeURIComponent(q)}` : ""}#tell-details`);
  };
  */

  return (
    <div className="ed-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="ed-hero" id="top">
          <div className="ed-hero-bg" style={{ backgroundImage: `url("${imgHero}")` }} aria-hidden="true"></div>
          <div className="ed-hero-overlay" aria-hidden="true"></div>

          <div className="ed-hero-inner">
            <div className="ed-hero-copy">
              <h1 data-reveal style={delay(0)}>
                <span className="ed-line">Your enterprise has</span>
                <span className="ed-line">the data.<em>Does it</em></span>
                <span className="ed-line"><em> have the answers?</em></span>
              </h1>
              <p data-reveal style={delay(1, 120)}>
                Orane helps organisations connect, organise and activate their enterprise data to deliver
                trusted insights, better decisions and measurable business outcomes.
              </p>
              <div className="ed-actions" data-reveal style={delay(2, 120)}>
                <Link href="/tellusyourproblem#tell-details" className="ed-btn ed-btn-primary">
                  Tell Us Your Data Challenge <span aria-hidden="true">→</span>
                </Link>
                <Link href="#capabilities" className="ed-btn ed-btn-outline">
                  Explore Our Data Solutions <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ============================= ASK ORANE =========================== */}
        {/*
        <section className="ed-ask" aria-label="Ask Orane">
          <div className="ed-container">
            <div className="ed-ask-grid">
              <div className="ed-ask-title" data-reveal>
                <h2>Ask Orane</h2>
                <p>Get answers. Find opportunities. Create value.</p>
              </div>

              <div className="ed-ask-main" data-reveal style={delay(1, 100)}>
                <form className="ed-ask-form" onSubmit={onAsk}>
                  <label className="ed-sr" htmlFor="ed-ask-input">Your enterprise data challenge</label>
                  <span className="ed-ask-lens"><Icon name="search" /></span>
                  <input
                    id="ed-ask-input"
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="What's your enterprise data challenge?"
                    autoComplete="off"
                  />
                  <button type="submit" className="ed-ask-go" aria-label="Send your challenge">
                    <Icon name="arrow" />
                  </button>
                </form>
                <ul className="ed-chips">
                  {questions.map((q) => (
                    <li key={q}>
                      <button type="button" className="ed-chip" onClick={() => setQuery(q)}>{q}</button>
                    </li>
                  ))}
                </ul>
              </div>

              <ul className="ed-ask-points" data-reveal style={delay(2, 100)}>
                {askPoints.map((p) => (
                  <li key={p.text}>
                    <span className="ed-ask-point-icon"><Icon name={p.icon} /></span>
                    <span>{p.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
        */}

        {/* =========================== CAPABILITIES ========================== */}
        <section className="ed-section" id="capabilities">
          <div className="ed-container">
            <div className="ed-head ed-head--row" data-reveal>
              <h2>Our enterprise data capabilities</h2>
              <p>From data strategy to AI-ready intelligence, we help you unlock more value from your data.</p>
            </div>

            <div className="ed-cap-grid">
              {capabilities.map((c, i) => (
                <article className="ed-cap" key={c.title} data-reveal style={delay(i % 3, 80)}>
                  <span className="ed-cap-icon"><Icon name={c.icon} /></span>
                  <h3>{c.title}</h3>
                  <p>{c.text}</p>
                  {/* <Link href={c.href} className="ed-link">Learn more <span aria-hidden="true">→</span></Link> */}
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== FRAGMENTED → INTELLIGENCE =================== */}
        <section className="ed-section" id="our-approach">
          <div className="ed-container">
            <div className="ed-turn">
              <div className="ed-turn-image" data-reveal>
                <img src={imgTurn} alt="Enterprise data brought together for business decisions" loading="lazy" />
              </div>

              <div className="ed-turn-copy" data-reveal style={delay(1, 100)}>
                <h2>Turn fragmented data into business intelligence.</h2>
                <p>
                  We bring together data from across your enterprise systems — SAP, CRM, SFA, HR, finance,
                  supply chain, manufacturing and external sources — and give it business context, so you can
                  understand what happened, why it happened and what to do next.
                </p>
                {/* <Link href="/how-we-work" className="ed-link ed-link--lg">
                  Explore our approach <span aria-hidden="true">→</span>
                </Link> */}

                <h3>Our solutions include:</h3>
                <ul className="ed-turn-list">
                  {solutions.map((s) => (
                    <li key={s.title}>
                      <span className="ed-solution-icon"><Icon name={s.icon} /></span>
                      <div>
                        <strong>{s.title}</strong>
                        <span>{s.text}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============================= PROCESS ============================= */}
        <section className="ed-section">
          <div className="ed-container">
            <div className="ed-head ed-head--row" data-reveal>
              <h2>From data to decisions — a practical path.</h2>
              <div className="ed-head-side">
                <p>We follow a structured approach to create a trusted and scalable data foundation.</p>
                {/* <Link href="/how-we-work" className="ed-link">See how it works <span aria-hidden="true">→</span></Link> */}
              </div>
            </div>

            <ol className="ed-path">
              {steps.map((s, i) => (
                <li className="ed-path-step" key={s.title} data-reveal style={delay(i, 90)}>
                  <span className="ed-path-no">{String(i + 1).padStart(2, "0")}</span>
                  <strong>{s.title}</strong>
                  <p>{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ============================ BENEFITS ============================= */}
        <section className="ed-section">
          <div className="ed-container">
            <div className="ed-head ed-head--row" data-reveal>
              <h2>Better information creates better outcomes.</h2>
              <p>Trusted data and analytics help you make faster, more confident decisions.</p>
            </div>

            <ul className="ed-benefits" data-reveal>
              {benefits.map((b) => (
                <li key={b.text}>
                  <span className="ed-benefit-icon"><Icon name={b.icon} /></span>
                  <span>{b.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ============================ USE CASES ============================ */}
        <section className="ed-section">
          <div className="ed-container">
            <div className="ed-head ed-head--row" data-reveal>
              <h2>Enterprise data in action</h2>
              {/* <Link href="/use-cases" className="ed-link">Explore more use cases <span aria-hidden="true">→</span></Link> */}
            </div>

            <div className="ed-uc-grid">
              {useCases.map((u, i) => (
                <article className="ed-uc" key={u.title} data-reveal style={delay(i % 3, 80)}>
                  <div className="ed-uc-image">
                    <img src={u.image} alt="" aria-hidden="true" loading="lazy" />
                  </div>
                  <div className="ed-uc-body">
                    <h3>{u.title}</h3>
                    <p>{u.text}</p>
                    {/* <Link href={u.href} className="ed-link">See how we help <span aria-hidden="true">→</span></Link> */}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============================== WHY ORANE ========================== */}
        <section className="ed-section">
          <div className="ed-container">
            <div className="ed-head ed-head--row" data-reveal>
              <h2>Why Orane for enterprise data?</h2>
              <p>We combine business understanding, enterprise systems knowledge and data expertise.</p>
            </div>

            <ul className="ed-why">
              {why.map((w, i) => (
                <li className="ed-why-item" key={w.title} data-reveal style={delay(i, 80)}>
                  <span className="ed-why-icon"><Icon name={w.icon} /></span>
                  <h3>{w.title}</h3>
                  <p>{w.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ============================ FINAL CTA ============================ */}
        <section className="ed-cta" id="contact">
          <img className="ed-cta-bg" src={imgCta} alt="" aria-hidden="true" />
          <div className="ed-cta-copy">
            <span className="ed-cta-eyebrow" data-reveal>YOUR DATA CAN DO MORE.</span>
            <h2 data-reveal style={delay(1, 120)}>Let&apos;s turn your data into better decisions.</h2>
            <p data-reveal style={delay(2, 120)}>
              Tell us what you&apos;re trying to achieve — better visibility, faster reporting, improved
              forecasting or an AI-ready data foundation. We&apos;ll help you define the right path forward.
            </p>
            <div className="ed-actions" data-reveal style={delay(3, 120)}>
              <Link href="/tellusyourproblem#tell-details" className="ed-btn ed-btn-primary">
                Tell Us Your Data Challenge <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}